export type FreshRequestOptions = {
    force?: boolean;
    maxAgeMs?: number;
    hasData?: boolean;
};

export const CORE_DATA_MAX_AGE_MS = 60_000;
export const IDENTITY_DATA_MAX_AGE_MS = 5 * 60_000;
export const PORTAL_DATA_MAX_AGE_MS = 60_000;

const loadedAtByKey = new Map<string, number>();
const inFlightByKey = new Map<string, Promise<unknown>>();

export function isRequestFresh(key: string, maxAgeMs = CORE_DATA_MAX_AGE_MS, now = Date.now()): boolean {
    const loadedAt = loadedAtByKey.get(key);
    return loadedAt !== undefined && now - loadedAt < maxAgeMs;
}

/**
 * Shares simultaneous reads and suppresses repeat reads for a short freshness
 * window. Stores remain responsible for holding the returned data; `hasData`
 * prevents a stale module-level marker from hiding an empty/reset store.
 */
export async function runFreshRequest<T>(
    key: string,
    loader: () => Promise<T>,
    options: FreshRequestOptions = {},
): Promise<T | undefined> {
    const maxAgeMs = options.maxAgeMs ?? CORE_DATA_MAX_AGE_MS;
    if (!options.force && options.hasData !== false && isRequestFresh(key, maxAgeMs)) {
        return undefined;
    }

    const existing = inFlightByKey.get(key) as Promise<T> | undefined;
    if (existing) {
        return await existing;
    }

    const request = loader().then((result) => {
        loadedAtByKey.set(key, Date.now());
        return result;
    });

    inFlightByKey.set(key, request);
    try {
        return await request;
    } finally {
        if (inFlightByKey.get(key) === request) {
            inFlightByKey.delete(key);
        }
    }
}

export function invalidateFreshRequest(keyOrPrefix?: string): void {
    if (!keyOrPrefix) {
        loadedAtByKey.clear();
        return;
    }

    for (const key of loadedAtByKey.keys()) {
        if (key === keyOrPrefix || key.startsWith(keyOrPrefix)) {
            loadedAtByKey.delete(key);
        }
    }
}

export function chunkRequestValues<T>(values: T[], size = 30): T[][] {
    if (size < 1) throw new Error('Chunk size must be at least 1.');
    const chunks: T[][] = [];
    for (let index = 0; index < values.length; index += size) {
        chunks.push(values.slice(index, index + size));
    }
    return chunks;
}
